import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as ChildIcon } from 'assets/icons/computer-scientist.svg'
import { getProfessionsCourse } from 'api/shortlister'
import { researchSessionStore } from 'shared/stores/researchSessionStore'
import { useVisionCareerPriorities } from '../../../hooks/useVisionCareerPriorities'
import { prepareCareersWithVision } from '../../../utils/prepareCareersWithVision'

export function useResearchCareer({
	activeScreen,
	subCategory,
	career,
	activeDegree,
	loadingDetails,
	awaitingCatalogDefaults,
	isSelectedCareer,
	setIsSelectedCareer,
	numCareerTitle,
	setNumCareerTitle,
}) {
	const [query, setQuery] = useState('')
	const [role, setRole] = useState('All careers')
	const [selectedIds, setSelectedIds] = useState([])
	const [sentMessage, setSentMessage] = useState('')

	useEffect(() => {
		if (!sentMessage) return undefined
		const timer = window.setTimeout(() => setSentMessage(''), 1000)
		return () => window.clearTimeout(timer)
	}, [sentMessage])

	const [professions, setProfessions] = useState([])
	const [loadingProfession, setLoadingProfession] = useState(true)
	const [professionsError, setProfessionsError] = useState(null)
	const isValidCareer = useRef(false)
	const professionsRequestGeneration = useRef(0)
	const [professionsRetryToken, setProfessionsRetryToken] = useState(0)

	const resetProfessions = useCallback(() => {
		professionsRequestGeneration.current += 1
		setProfessions([])
		setLoadingProfession(true)
		isValidCareer.current = false
		setProfessionsError(null)
	}, [])

	//Get Profession
	useEffect(() => {
		if (loadingDetails || awaitingCatalogDefaults) {
			setLoadingProfession(true)
			return
		}

		if (!subCategory) {
			setProfessions([])
			setLoadingProfession(false)
			isValidCareer.current = false
			setProfessionsError(null)
			return
		}

		setLoadingProfession(true)
		isValidCareer.current = false
		setProfessionsError(null)
		const gen = ++professionsRequestGeneration.current
		const subCategoryForRequest = subCategory
		const degreeForRequest = activeDegree || 'Bachelors'

		const fetchProfessions = async () => {
			try {
				setProfessions([])
				const fetchedProfessions = await getProfessionsCourse(
					subCategoryForRequest,
					degreeForRequest,
				)

				if (gen !== professionsRequestGeneration.current) return

				const professionRows = fetchedProfessions?.data?.[0]
				if (!Array.isArray(professionRows)) {
					setProfessions([])
					setProfessionsError(null)
					return
				}

				const updatedProfessions = professionRows.map(profession => {
					const personalAttributes = JSON.parse(
						profession?.profession_personal_attributes?.replace(/'/g, '"'),
					)
					return {
						...profession,
						profession_personal_attributes: personalAttributes,
					}
				})
				setProfessions(updatedProfessions)
				setProfessionsError(null)
			} catch (error) {
				if (gen !== professionsRequestGeneration.current) return
				console.error('Error fetching professions:', error)
				setProfessions([])
				setProfessionsError(error)
			} finally {
				if (gen === professionsRequestGeneration.current) {
					setLoadingProfession(false)
				}
			}
		}

		fetchProfessions()
	}, [
		subCategory,
		activeDegree,
		loadingDetails,
		awaitingCatalogDefaults,
		professionsRetryToken,
	])

	//Get Default career
	useEffect(() => {
		if (loadingDetails || loadingProfession || isValidCareer?.current) return

		if (!subCategory) {
			if (isSelectedCareer && career) {
				isValidCareer.current = true
			} else {
				// Avoid updateDraft on already-empty career — it can mark isDirty
				// before category seed and block seedDraftIfEmpty.
				if (career) {
					researchSessionStore.updateDraft({ career: '' })
				}
				setNumCareerTitle(null)
				setIsSelectedCareer(false)
			}
		} else if (career && professions?.length) {
			const isCareerValid = professions.some(
				prof => prof.profession_title === career,
			)

			if (isCareerValid) {
				const index = professions.findIndex(el => el.profession_title === career)

				if (index >= 0 && index !== numCareerTitle) {
					setNumCareerTitle(index)
				}
			} else if (!isSelectedCareer) {
				researchSessionStore.updateDraft({
					career: professions[0].profession_title,
				})
				setNumCareerTitle(0)
			}
			isValidCareer.current = true
		} else if (subCategory && !career && professions?.length) {
			if (professionsError) return

			const seeded = researchSessionStore.seedCareerIfEmpty({
				career: professions[0].profession_title,
			})
			if (!seeded) {
				researchSessionStore.updateDraft({
					career: professions[0].profession_title,
				})
			}
			setNumCareerTitle(0)
			isValidCareer.current = true
		}
	}, [
		subCategory,
		career,
		professions,
		loadingProfession,
		loadingDetails,
		isSelectedCareer,
		numCareerTitle,
		professionsError,
		setIsSelectedCareer,
		setNumCareerTitle,
	])

	useEffect(() => {
		setRole('All careers')
	}, [subCategory])

	const retryProfessions = useCallback(() => {
		if (loadingProfession) return
		resetProfessions()
		setProfessionsRetryToken(token => token + 1)
	}, [loadingProfession, resetProfessions])

	const { careerVisionById } = useVisionCareerPriorities({ professions })

	const preparedProfessions = useMemo(
		() => prepareCareersWithVision(professions, careerVisionById),
		[professions, careerVisionById],
	)

	const careers = useMemo(
		() =>
			preparedProfessions.map(profession => ({
				id: String(profession.profession_id),
				icon: <ChildIcon />,
				title: profession.profession_title || '',
				salary:
					profession.avg_salary != null && profession.avg_salary !== ''
						? `$${Number(profession.avg_salary).toLocaleString('en-US', {
								maximumFractionDigits: 0,
							})}/yr`
						: '',
				demand: profession.profession_prospects || '',
				description: profession.profession_description || '',
				skills: Array.isArray(profession.profession_personal_attributes)
					? profession.profession_personal_attributes
					: [],
				isVisionTop: Boolean(profession.isVisionTop),
				countriesVision: profession.countriesVision || [],
			})),
		[preparedProfessions],
	)

	const selectedCareers = careers.filter(careerItem =>
		selectedIds.includes(careerItem.id),
	)
	const filteredCareers = careers.filter(careerItem => {
		const matchesQuery =
			`${careerItem.title} ${careerItem.description} ${(careerItem.skills || []).join(' ')}`
				.toLowerCase()
				.includes(query.trim().toLowerCase())
		const matchesRole = role === 'All careers' || careerItem.title === role
		return matchesQuery && matchesRole
	})

	const toggleCareer = id => {
		setSentMessage('')
		setSelectedIds(current =>
			current.includes(id)
				? current.filter(item => item !== id)
				: [...current, id],
		)
	}

	const clearSelectedCareers = () => setSelectedIds([])

	const replaceSelectedIds = useCallback(ids => {
		setSelectedIds(Array.isArray(ids) ? ids : [])
	}, [])

	return {
		professions,
		loadingProfession,
		professionsError,
		resetProfessions,
		retryProfessions,
		isValidCareer,
		careers,
		filteredCareers,
		query,
		setQuery,
		role,
		setRole,
		selectedIds,
		selectedCareers,
		toggleCareer,
		clearSelectedCareers,
		replaceSelectedIds,
		sentMessage,
		setSentMessage,
	}
}
